import { useMemo } from 'react';
import './EventParticipationManage.scss';
import {
	useDeleteParticipationForUser,
	useGetOneEventForAdmin,
	useGetUsersToCheckOffForEvent,
	useUpdateParticipationStatusForUser,
} from '../../Core/Services/event';
import { EventParticipator, ParticipationStatus } from '../../Core/Types/event';
import { useTranslation } from 'react-i18next';
import { useGlobalState } from '../../Core/Contexts/globalStateContext';

import { hasModuleAccess } from '../../Core/Services/access';
import { AccessTypeRight } from '../../Core/Types/access';

type StatusInfo = {
	labelKey: string;
	colorKey: string;
	color: string;
	bgColorKey: string;
	bgColor: string;
	iconKey: string;
	icon: string;
	confirmHeaderKey?: string;
	confirmTextKey?: string;
};

const STATUS_CONFIG: Record<number, StatusInfo> = {
	[ParticipationStatus.ConfirmedAttended]: {
		labelKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_ATTENDED',
		colorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_ATTENDED_COLOR',
		color: '#0f9d58',
		bgColorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_ATTENDED_BG_COLOR',
		bgColor: '#e7f6ed',
		iconKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_ATTENDED_ICON',
		icon: 'fas fa-check-circle',
	},
	[ParticipationStatus.Attending]: {
		labelKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_ATTENDING',
		colorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_ATTENDING_COLOR',
		color: '#2563eb',
		bgColorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_ATTENDING_BG_COLOR',
		bgColor: '#dbeafe',
		iconKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_ATTENDING_ICON',
		icon: 'far fa-calendar-check',
		confirmHeaderKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_ATTENDING_CONFIRM_HEADER',
		confirmTextKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_ATTENDING_CONFIRM_TEXT',
	},
	[ParticipationStatus.Waitlisted]: {
		labelKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_WAITLISTED',
		colorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_WAITLISTED_COLOR',
		color: '#b07d00',
		bgColorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_WAITLISTED_BG_COLOR',
		bgColor: '#fff6df',
		iconKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_WAITLISTED_ICON',
		icon: 'fas fa-clock',
	},
	[ParticipationStatus.NotAttending]: {
		labelKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_NOT_ATTENDING',
		colorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_NOT_ATTENDING_COLOR',
		color: '#dc2626',
		bgColorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_NOT_ATTENDING_BG_COLOR',
		bgColor: '#fee2e2',
		iconKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_NOT_ATTENDING_ICON',
		icon: 'fas fa-times-circle',
	},
	[ParticipationStatus.Pending]: {
		labelKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_PENDING',
		colorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_PENDING_COLOR',
		color: '#7c3aed',
		bgColorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_PENDING_BG_COLOR',
		bgColor: '#ede9fe',
		iconKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_PENDING_ICON',
		icon: 'fas fa-hourglass-half',
	},
	[ParticipationStatus.Invited]: {
		labelKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_INVITED',
		colorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_INVITED_COLOR',
		color: '#0891b2',
		bgColorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_INVITED_BG_COLOR',
		bgColor: '#cffafe',
		iconKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_INVITED_ICON',
		icon: 'fas fa-envelope',
	},
	[ParticipationStatus.None]: {
		labelKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_NONE',
		colorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_NONE_COLOR',
		color: '#6b7280',
		bgColorKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_NONE_BG_COLOR',
		bgColor: '#f3f4f6',
		iconKey: 'EVENT.PARTICIPATION_MANAGE.STATUS_NONE_ICON',
		icon: 'fas fa-minus-circle',
	},
};

const ORDERED_GROUP_STATUSES = [
	ParticipationStatus.ConfirmedAttended,
	ParticipationStatus.Attending,
	ParticipationStatus.Waitlisted,
	ParticipationStatus.NotAttending,
] as const;

const SELECTABLE_STATUSES: ParticipationStatus[] = [
	ParticipationStatus.ConfirmedAttended,
	ParticipationStatus.Attending,
	ParticipationStatus.Waitlisted,
	ParticipationStatus.NotAttending,
	//ParticipationStatus.Pending,
];

type StatusGroup = {
	key: string | number;
	status: ParticipationStatus | null;
	label: string;
	participants: EventParticipator[];
	capacityLabel?: string;
};

const getStatusInfo = (status: ParticipationStatus): StatusInfo =>
	STATUS_CONFIG[status] ?? STATUS_CONFIG[ParticipationStatus.None];

const getResolvedStatusInfo = (status: ParticipationStatus, t: ReturnType<typeof useTranslation>['t']): StatusInfo => {
	const info = getStatusInfo(status);
	return {
		...info,
		color: t(info.colorKey, { defaultValue: info.color }),
		bgColor: t(info.bgColorKey, { defaultValue: info.bgColor }),
		icon: t(info.iconKey, { defaultValue: info.icon }),
	};
};

const getStatusConfirmInfo = (
	status: ParticipationStatus,
	t: ReturnType<typeof useTranslation>['t'],
	i18n: ReturnType<typeof useTranslation>['i18n']
) => {
	const info = getStatusInfo(status);

	if (!info.confirmTextKey || !i18n.exists(info.confirmTextKey)) {
		return null;
	}

	return {
		title: info.confirmHeaderKey && i18n.exists(info.confirmHeaderKey) ? t(info.confirmHeaderKey) : undefined,
		message: t(info.confirmTextKey),
	};
};

type EventParticipationManageProps = {
	eventId: number;
	groupByOrganizer?: boolean;
};

const EventParticipationManage = ({ eventId, groupByOrganizer = false }: EventParticipationManageProps) => {
	const { t, i18n } = useTranslation();
	const { showConfirm, state } = useGlobalState();
	const canDeleteParticipation = hasModuleAccess(state.user, 'EVENT', AccessTypeRight.Admin);
	const deletion = useDeleteParticipationForUser();
	const evnt = useGetOneEventForAdmin(eventId, groupByOrganizer === true);
	const participants = useGetUsersToCheckOffForEvent(eventId);
	const { updateParticipationStatusForUser, statusState, updatingUserId } = useUpdateParticipationStatusForUser();

	const eventHasPassed = evnt ? new Date() > new Date(evnt.EndDateTimeUtc) : false;
	const maxParticipants = evnt?.MaxParticipants ?? 0;

	const groups = useMemo<StatusGroup[]>(() => {
		if (!participants) return [];

		if (groupByOrganizer) {
			if (!evnt) return [];

			const organizerEntries = [evnt.Organizer, ...(evnt.CoOrganizers ?? [])].reduce<Array<{ id: number; name: string }>>(
				(acc, organizer) => {
					if (!organizer || acc.some((entry) => entry.id === organizer.Id)) return acc;
					const organizerName =
						[organizer.FirstName, organizer.LastName].filter(Boolean).join(' ') || organizer.UserName;
					acc.push({ id: organizer.Id, name: organizerName });
					return acc;
				},
				[]
			);

			const organizerIds = new Set(organizerEntries.map((organizer) => organizer.id));
			const groupedParticipants = new Map<number, EventParticipator[]>();
			const unassignedParticipants: EventParticipator[] = [];

			participants.forEach((participant) => {
				const matchedOrganizerId = participant.AllUserRelations?.find((relation) => {
					return organizerIds.has(relation.ParentUserId) || organizerIds.has(relation.ChildUserId);
				});
				const organizerId = matchedOrganizerId
					? organizerIds.has(matchedOrganizerId.ParentUserId)
						? matchedOrganizerId.ParentUserId
						: matchedOrganizerId.ChildUserId
					: null;

				if (organizerId === null) {
					unassignedParticipants.push(participant);
					return;
				}

				const organizerParticipants = groupedParticipants.get(organizerId) ?? [];
				organizerParticipants.push(participant);
				groupedParticipants.set(organizerId, organizerParticipants);
			});

			const organizerGroups = organizerEntries
				.map<StatusGroup>((organizer) => ({
					key: `organizer-${organizer.id}`,
					status: null,
					label: organizer.name,
					participants: groupedParticipants.get(organizer.id) ?? [],
				}))
				.filter((group) => group.participants.length > 0);

			if (unassignedParticipants.length > 0) {
				organizerGroups.push({
					key: 'other',
					status: null,
					label: t('EVENT.PARTICIPATION_MANAGE.GROUP_OTHER'),
					participants: unassignedParticipants,
				});
			}

			if (organizerGroups.length > 1) return organizerGroups;
		}

		const mainGroupStatuses = new Set<number>(ORDERED_GROUP_STATUSES);

		const mainGroups: StatusGroup[] = ORDERED_GROUP_STATUSES
			// When the event has passed, skip Attending as a standalone group —
			// those users are merged into ConfirmedAttended below.
			.filter((status) => !(eventHasPassed && status === ParticipationStatus.Attending))
			.map((status) => {
				const isConfirmedGroup = status === ParticipationStatus.ConfirmedAttended;
				const groupParticipants =
					eventHasPassed && isConfirmedGroup
						? participants.filter(
								(p) =>
									p.Status === ParticipationStatus.ConfirmedAttended ||
									p.Status === ParticipationStatus.Attending
							)
						: participants.filter((p) => p.Status === status);

				const showCapacity =
					maxParticipants > 0 &&
					((eventHasPassed && isConfirmedGroup) || (!eventHasPassed && status === ParticipationStatus.Attending));

				return {
					key: status,
					status,
					label: t(STATUS_CONFIG[status]?.labelKey ?? ''),
					participants: groupParticipants,
					capacityLabel: showCapacity ? `/ ${maxParticipants}` : undefined,
				};
			})
			.filter((g) => g.participants.length > 0);

		const otherParticipants = participants.filter((p) => !mainGroupStatuses.has(p.Status));

		if (otherParticipants.length > 0) {
			mainGroups.push({
				key: 'other',
				status: null,
				label: t('EVENT.PARTICIPATION_MANAGE.GROUP_OTHER'),
				participants: otherParticipants,
			});
		}

		return mainGroups;
	}, [participants, groupByOrganizer, evnt, eventHasPassed, maxParticipants, t]);

	if (!participants) return null;

	const isUpdating = statusState === 'LOADING' || deletion.statusState === 'LOADING';

	const handleDelete = (participant: EventParticipator) => {
		if (!canDeleteParticipation || participant.ParticipantUserId <= 0 || isUpdating) return;
		showConfirm({
			title: t('EVENT.PARTICIPATION_MANAGE.DELETE_CONFIRM_TITLE', { defaultValue: 'Delete participation?' }),
			message: t('EVENT.PARTICIPATION_MANAGE.DELETE_CONFIRM_TEXT', {
				defaultValue:
					'Permanently delete participation and all participation history for {{name}} in this event? This cannot be undone. The user account will remain. If the user still matches automatic enrollment permissions, they may be added again.',
				name: participant.ParticipantName,
			}),
			onConfirm: () => deletion.deleteParticipationForUser(eventId, participant.ParticipantUserId),
			legacy: false,
		});
	};

	const handleStatusChange = (userId: number, newStatus: ParticipationStatus) => {
		if (isUpdating) return;

		const runStatusChange = () => updateParticipationStatusForUser(eventId, userId, newStatus);
		const confirmInfo = getStatusConfirmInfo(newStatus, t, i18n);

		if (!confirmInfo) {
			runStatusChange();
			return;
		}

		showConfirm({
			title: confirmInfo.title,
			message: confirmInfo.message,
			onConfirm: runStatusChange,
			legacy: false,
		});
	};

	return (
		<div className='biz-event-participation-manage admin-panel'>
			{deletion.errorMessage && <p role='alert'>{deletion.errorMessage}</p>}
			{participants.length === 0 && <p className='biz-epm-empty'>{t('EVENT.PARTICIPATION_MANAGE.NO_PARTICIPANTS')}</p>}
			{groups.map((group) => (
				<div key={group.key} className='biz-epm-group'>
					<div className='biz-epm-group-header'>
						{group.status !== null && (
							<span
								className='biz-epm-group-dot'
								style={{ background: getResolvedStatusInfo(group.status, t).color }}
							/>
						)}
						<span className='biz-epm-group-title'>{group.label}</span>
						<span className='biz-epm-group-count'>
							{group.participants.length}
							{group.capacityLabel && <span className='biz-epm-group-capacity'>{group.capacityLabel}</span>}
						</span>
					</div>
					<div className='biz-epm-participant-list'>
						{group.participants.map((pp) => {
							const info = getResolvedStatusInfo(pp.Status, t);
							const isThisUpdating =
								updatingUserId === pp.ParticipantUserId ||
								(deletion.statusState === 'LOADING' && deletion.variables?.userId === pp.ParticipantUserId);
							return (
								<div
									key={pp.ParticipantUserId}
									className={`biz-epm-participant-row${isThisUpdating ? ' biz-epm-participant-row--updating' : ''}`}
								>
									<div className='biz-epm-participant-info'>
										<span className='biz-epm-participant-name'>{pp.ParticipantName}</span>
										{(pp.UserComment || pp.FoodPreference) && (
											<div className='biz-epm-participant-details'>
												{pp.UserComment && (
													<span className='biz-epm-participant-comment'>{pp.UserComment}</span>
												)}
												{pp.FoodPreference && (
													<span className='biz-epm-participant-food' title={pp.FoodPreference}>
														<i className='fas fa-utensils' />
														<span>{pp.FoodPreference}</span>
													</span>
												)}
											</div>
										)}
									</div>
									<div className='biz-epm-status-selector'>
										<div
											className='biz-epm-status-badge'
											style={{
												color: info.color,
												background: info.bgColor,
												borderColor: info.color,
											}}
										>
											{isThisUpdating ? (
												<i className='fas fa-spinner fa-spin' />
											) : (
												<i className={info.icon} />
											)}
											<span>{t(info.labelKey)}</span>
											<i className='biz-epm-chevron fas fa-chevron-down' />
										</div>
										<div className='biz-epm-status-options'>
											{SELECTABLE_STATUSES.filter(
												(s) => evnt?.IsWaitlistEnabled || s !== ParticipationStatus.Waitlisted
											).map((s) => {
												const opt = getResolvedStatusInfo(s, t);
												const isActive = pp.Status === s;
												return (
													<button
														key={s}
														className={`biz-epm-option${isActive ? ' biz-epm-option--active' : ''}`}
														style={
															{
																'--opt-color': opt.color,
																'--opt-bg': opt.bgColor,
															} as React.CSSProperties
														}
														onClick={() => !isActive && handleStatusChange(pp.ParticipantUserId, s)}
														disabled={isUpdating}
														title={t(opt.labelKey)}
													>
														<i className={opt.icon} />
														<span>{t(opt.labelKey)}</span>
													</button>
												);
											})}
											{canDeleteParticipation && pp.ParticipantUserId > 0 && (
												<button
													type='button'
													className='biz-epm-option'
													style={
														{ '--opt-color': '#dc2626', '--opt-bg': '#fee2e2' } as React.CSSProperties
													}
													disabled={isUpdating}
													onClick={() => handleDelete(pp)}
												>
													<i className='fas fa-trash-alt' />
													<span>
														{t('EVENT.PARTICIPATION_MANAGE.DELETE', {
															defaultValue: 'Delete participation',
														})}
													</span>
												</button>
											)}
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			))}
		</div>
	);
};

export default EventParticipationManage;
